import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  Collapse,
  TextField,
  Select,
  MenuItem,
  ClickAwayListener,
  Grow,
  Popper,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  EditOutlined,
  ReceiptLongOutlined,
  SummarizeOutlined,
} from "@mui/icons-material";
import { useTheme } from "../../ThemeContext";
import { usePermissions } from "../../permissions/PermissionsProvider";
import { ACTIONS } from "../../permissions/definition";
import { deriveInvoiceRaised } from "../../utils/status";
import { CompanyFlag } from "../icons/CompanyFlags";

const ProjectTable = ({
  projects,
  selectedProjects,
  expandedRows,
  onToggleRow,
  onToggleProject,
  onSelectAll,
  onProjectRefClick,
  onClientNavigate,
  onEditProject,
  onCreateInvoiceForProject,
  onGenerateSummary,
  onCellClick,
  editableCell,
  editableTaskCell,
  onInputChange,
  onBlur,
  onInvoicePreview,
  taskOptions,
  columnWidth,
  rowHeight,
  fontSize,
  firstColumnPadding,
  countryColumnPadding,
  subjectColumnPadding,
  emptyPlaceholder = "—",
  formatMoney,
  dateformatter,
  convertTimestampToDate,
  bodyRef: externalBodyRef,
  loadMoreRef,
  hasMore,
  isLoading,
  isLoadingMore,
  sortColumn,
  sortOrder,
  onSort,
}) => {
  const { theme } = useTheme();
  const { can } = usePermissions();
  const canSeeFinancials = can(ACTIONS.FINANCIALS_VIEW);
  const canEditProjects = can(ACTIONS.PROJECTS_EDIT);
  const canCreateInvoices = can(ACTIONS.INVOICES_CREATE);

  const renderSortIndicator = (field) =>
    sortColumn === field ? (
      <span className="ml-1 text-xs">{sortOrder === "asc" ? "▲" : "▼"}</span>
    ) : null;

  // base columns (8) + optional financials (8)
  const totalColumns = canSeeFinancials ? 16 : 8;
  const tableFontFamily = "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif";
  const headerFontSize = "14px";
  const bodyFontSize = "12.5px";
  const taskColumnWidths = {
    taskNo: "80px",
    subject: "145px",
    country: "120px",
    task: "100px",
    orderAccepted: "130px",
    dueDate: "110px",
    status: "100px",
    completedOn: "120px",
    analyst1: "90px",
    analyst2: "90px",
    income: "70px",
    vat: "60px",
    amountIncVat: "130px",
  };

  const internalBodyRef = React.useRef(null);
  const bodyRef = externalBodyRef || internalBodyRef;

  const renderPlaceholder = (value) => {
    if (value === null || value === undefined) return emptyPlaceholder;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "-") return emptyPlaceholder;
    }
    return value;
  };

  const toPx = (value) => (typeof value === "number" ? `${value}px` : value);

  const getTimeValue = (value) => {
    const asDate = convertTimestampToDate(value);
    if (asDate instanceof Date && !Number.isNaN(asDate.getTime())) {
      return asDate.getTime();
    }
    if (
      value &&
      typeof value === "object" &&
      typeof value.seconds === "number"
    ) {
      return value.seconds * 1000;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const getLatestInvoice = (project) => {
    const invoices = Array.isArray(project?.invoices) ? project.invoices : [];
    if (!invoices.length) return null;
    return [...invoices].sort(
      (a, b) => getTimeValue(b?.createdAt) - getTimeValue(a?.createdAt)
    )[0];
  };

  const normalizeNumber = (value) => {
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (typeof value === "string" && value.trim().endsWith("px")) {
      const parsed = parseFloat(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    if (typeof value === "string") {
      const parsed = parseFloat(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  // Narrower leading columns (checkbox + expander)
  const baseSideWidth = normalizeNumber(columnWidth) ?? 14;
  const firstColumnWidth = `${baseSideWidth / 3.1}px`;

  const baseColumns = [
    firstColumnWidth, // checkbox
    firstColumnWidth, // expander
    "120px", // Project Ref
    "80px", // Created At
    "80px", // Proj Mgr
    "80px", // Proj Status
    "140px", // Client
    "110px", // PoC
  ];

  const financialColumns = [
    "70px", // Fee
    "100px", // Fee Inc VAT (with VAT displayed below)
    "80px", // Inv Raised
    "80px", // Inv Date
    "80px", // Pay Status
    "80px", // Paid On
    "90px", // Pay Notes / Client Ref
    "100px", // Inv Link
  ];

  const colWidths = canSeeFinancials
    ? [...baseColumns, ...financialColumns]
    : [...baseColumns];

  const colWidthsTotal = colWidths.reduce((sum, width) => {
    const num = normalizeNumber(width);
    return sum + (Number.isFinite(num) ? num : 0);
  }, 0);

  const computedWidth = colWidthsTotal > 0 ? colWidthsTotal : 1100;
  const tableWidth = `${computedWidth}px`;

  const [hasVerticalScroll, setHasVerticalScroll] = React.useState(false);
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);
  const [contextMenu, setContextMenu] = React.useState(null);
  const contextMenuAnchorRef = React.useRef(null);

  React.useEffect(() => {
    const measure = () => {
      const el = bodyRef.current;
      if (!el) return;
      const width = el.offsetWidth - el.clientWidth;
      const hasScroll = el.scrollHeight > el.clientHeight;
      setScrollbarWidth(width > 0 ? width : 0);
      setHasVerticalScroll(hasScroll);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [projects.length, expandedRows, rowHeight, tableWidth]);

  const handleProjectRefContextMenu = (event, projectId) => {
    event.preventDefault();
    event.stopPropagation();
    // Create a virtual element for Popper positioning
    const virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: event.clientY,
        right: event.clientX,
        bottom: event.clientY,
        left: event.clientX,
        x: event.clientX,
        y: event.clientY,
      }),
    };
    contextMenuAnchorRef.current = virtualElement;
    setContextMenu({ projectId });
  };

  const handleContextMenuClose = () => setContextMenu(null);

  const handleContextMenuAction = (action) => {
    if (!contextMenu) return;

    if (action === "edit" && canEditProjects && onEditProject) {
      onEditProject(contextMenu.projectId);
    }

    if (
      action === "invoice" &&
      canCreateInvoices &&
      onCreateInvoiceForProject
    ) {
      onCreateInvoiceForProject(contextMenu.projectId);
    }

    if (action === "summary" && onGenerateSummary) {
      onGenerateSummary(contextMenu.projectId);
    }

    handleContextMenuClose();
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        px: { xs: 1.5, md: 3, xl: 5 },
        pb: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "100%", mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            boxShadow: "0px 10px 30px -20px rgba(15,23,42,0.45)",
            backgroundColor: "#fff",
            overflow: "hidden",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: tableWidth,
                mx: "auto",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: tableWidth,
                  boxSizing: "border-box",
                  pr: hasVerticalScroll ? `${scrollbarWidth}px` : 0,
                  position: "relative",
                  backgroundColor: theme.tableHeaderBg,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  overflow: "hidden",
                  "&::after": {
                    content: '""',
                    display: hasVerticalScroll ? "block" : "none",
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: `${scrollbarWidth}px`,
                    height: "100%",
                    backgroundColor: theme.tableHeaderBg,
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 4,
                    pointerEvents: "none",
                  },
                }}
              >
                <Table
                  aria-label="projects header"
                  sx={{
                    minWidth: tableWidth,
                    width: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "collapse",
                    fontFamily: tableFontFamily,
                    "& th": {
                      fontSize: headerFontSize,
                      fontWeight: 600,
                      fontFamily: tableFontFamily,
                    },
                  }}
                >
                  <colgroup>
                    {colWidths.map((w, idx) => (
                      <col key={idx} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableHead
                    sx={{
                      "& th": {
                        backgroundColor: theme.tableHeaderBg,
                        color: "#f8fafc",
                        fontWeight: 700,
                      },
                      "& th:first-of-type": { borderTopLeftRadius: 0 },
                      "& th:last-child": { borderTopRightRadius: 0 },
                    }}
                  >
                    {/* Group Headers Row */}
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 700,
                          letterSpacing: "1.5px",
                          textTransform: "uppercase",
                          background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 100%), ${theme.tableHeaderBg}`,
                          borderBottom: "2px solid rgba(255,255,255,0.25)",
                          borderRight: "1px solid rgba(255,255,255,0.3)",
                          py: 0.75,
                          color: "rgba(255,255,255,0.95)",
                          textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        }}
                      >
                        Project Info
                      </TableCell>
                      <TableCell
                        colSpan={2}
                        sx={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 700,
                          letterSpacing: "1.5px",
                          textTransform: "uppercase",
                          background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 100%), ${theme.tableHeaderBg}`,
                          borderBottom: "2px solid rgba(255,255,255,0.25)",
                          borderRight: "1px solid rgba(255,255,255,0.3)",
                          py: 0.75,
                          color: "rgba(255,255,255,0.95)",
                          textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        }}
                      >
                        Client Info
                      </TableCell>
                      {canSeeFinancials && (
                        <>
                          <TableCell
                            colSpan={2}
                            sx={{
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 700,
                              letterSpacing: "1.5px",
                              textTransform: "uppercase",
                              background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 100%), ${theme.tableHeaderBg}`,
                              borderBottom: "2px solid rgba(255,255,255,0.25)",
                              borderRight: "1px solid rgba(255,255,255,0.3)",
                              py: 0.75,
                              color: "rgba(255,255,255,0.95)",
                              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                            }}
                          >
                            Financial Info
                          </TableCell>
                          <TableCell
                            colSpan={6}
                            sx={{
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 700,
                              letterSpacing: "1.5px",
                              textTransform: "uppercase",
                              background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 100%), ${theme.tableHeaderBg}`,
                              borderBottom: "2px solid rgba(255,255,255,0.25)",
                              py: 0.75,
                              color: "rgba(255,255,255,0.95)",
                              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                            }}
                          >
                            Invoice and Payment
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                    {/* Column Headers Row */}
                    <TableRow>
                      <TableCell
                        sx={{
                          width: firstColumnWidth,
                          padding: "0px",
                          paddingLeft: `${Math.max(
                            firstColumnPadding - 6,
                            4
                          )}px`,
                          paddingRight: "4px",
                          textAlign: "center",
                        }}
                      >
                        <Checkbox
                          indeterminate={
                            selectedProjects.length > 0 &&
                            selectedProjects.length < projects.length
                          }
                          checked={
                            projects.length > 0 &&
                            selectedProjects.length === projects.length
                          }
                          onChange={onSelectAll}
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            "&.Mui-checked": {
                              color: "#fff",
                            },
                            "&.MuiCheckbox-indeterminate": {
                              color: "#fff",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: firstColumnWidth }} />
                      <TableCell
                        sx={{
                          width: "100px",
                          height: rowHeight,
                          fontSize: headerFontSize,
                          fontWeight: 600,
                          padding: "0px",
                          paddingLeft: "0px",
                          paddingRight: "8px",
                          textAlign: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => onSort("projectRef")}
                      >
                        Project Ref{renderSortIndicator("projectRef")}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "90px",
                          height: rowHeight,
                          fontSize: headerFontSize,
                          fontWeight: 600,
                          padding: "0px",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          textAlign: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => onSort("createdAt")}
                      >
                        Proj Date{renderSortIndicator("createdAt")}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "80px",
                          height: rowHeight,
                          fontSize: headerFontSize,
                          fontWeight: 600,
                          padding: "0px",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          textAlign: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => onSort("projectManager")}
                      >
                        Proj Mgr{renderSortIndicator("projectManager")}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "80px",
                          height: rowHeight,
                          fontSize: headerFontSize,
                          fontWeight: 600,
                          padding: "0px",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          textAlign: "center",
                          borderRight: "1px solid rgba(255,255,255,0.2)",
                        }}
                      >
                        Proj Status
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          height: rowHeight,
                          fontSize: headerFontSize,
                          fontWeight: 600,
                          padding: "0px",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          textAlign: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => onSort("client")}
                      >
                        Client{renderSortIndicator("client")}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "160px",
                          height: rowHeight,
                          fontSize: headerFontSize,
                          fontWeight: 600,
                          padding: "0px",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          textAlign: "center",
                          borderRight: canSeeFinancials
                            ? "1px solid rgba(255,255,255,0.2)"
                            : "none",
                        }}
                      >
                        PoC
                      </TableCell>
                      {canSeeFinancials && (
                        <>
                          <TableCell
                            sx={{
                              width: "70px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={() => onSort("income")}
                          >
                            Fee{renderSortIndicator("income")}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "110px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "8px",
                              paddingRight: "8px",
                              textAlign: "center",
                              cursor: "pointer",
                              userSelect: "none",
                              borderRight: "1px solid rgba(255,255,255,0.2)",
                            }}
                            onClick={() => onSort("amountIncVat")}
                          >
                            Fee Inc VAT{renderSortIndicator("amountIncVat")}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "80px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                          >
                            Inv Raised
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "80px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                          >
                            Inv Date
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "100px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                          >
                            Pay Status
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "100px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                          >
                            Paid On
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "100px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                          >
                            Client Ref
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "100px",
                              height: rowHeight,
                              fontSize: headerFontSize,
                              fontWeight: 600,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                          >
                            Inv Link
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                </Table>
              </Box>

              <TableContainer
                component={Box}
                ref={bodyRef}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: tableWidth,
                  maxHeight: {
                    xs: "calc(100vh - 360px)",
                    md: "calc(100vh - 400px)",
                  },
                  overflowX: "hidden",
                  overflowY: "auto",
                  backgroundColor: "#fff",
                }}
              >
                <Table
                  aria-label="projects table"
                  sx={{
                    minWidth: tableWidth,
                    width: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "collapse",
                    fontFamily: tableFontFamily,
                    "& td": {
                      fontSize: bodyFontSize,
                      fontFamily: tableFontFamily,
                    },
                  }}
                >
                  <colgroup>
                    {colWidths.map((w, idx) => (
                      <col key={idx} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    {projects.map((project, index) => (
                      <React.Fragment key={project.id}>
                        <TableRow
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "white" : "#f0f0f0",
                          }}
                        >
                          <TableCell
                            sx={{
                              width: firstColumnWidth,
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: Math.max(firstColumnPadding - 6, 4),
                              paddingRight: "4px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                            }}
                          >
                            <Checkbox
                              checked={selectedProjects.includes(project.id)}
                              onChange={(event) =>
                                onToggleProject(event, project.id)
                              }
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              width: firstColumnWidth,
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: Math.max(firstColumnPadding - 6, 4),
                              paddingRight: "4px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "4px",
                                left: "8px",
                                backgroundColor: "blue",
                                color: "white",
                                borderRadius: "50%",
                                width: "18px",
                                height: "18px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: "bold",
                              }}
                            >
                              {project.tasks ? project.tasks.length : 0}
                            </div>
                            <IconButton
                              size="small"
                              onClick={() => onToggleRow(project.id)}
                              style={{ cursor: "pointer" }}
                            >
                              {expandedRows[project.id] ? (
                                <ExpandLess />
                              ) : (
                                <ExpandMore />
                              )}
                            </IconButton>
                          </TableCell>

                          <TableCell
                            sx={{
                              width: "100px",
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: "4px",
                              paddingRight: "8px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                              }}
                            >
                              <CompanyFlag
                                company={project.company}
                                size={11}
                              />
                              <Typography
                                noWrap
                                component="span"
                                href="#"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onProjectRefClick(project.id);
                                }}
                                onContextMenu={(e) =>
                                  handleProjectRefContextMenu(e, project.id)
                                }
                                sx={{
                                  color: "#1d4ed8",
                                  cursor: "pointer",
                                  fontSize: bodyFontSize,
                                  textDecoration: "none",
                                  "&:hover": {
                                    textDecoration: "underline",
                                    color: "#1d4ed8",
                                  },
                                }}
                              >
                                {project.projectRef}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell
                            sx={{
                              width: "90px",
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                            }}
                          >
                            {dateformatter(
                              convertTimestampToDate(project.createdAt)
                            )}
                          </TableCell>

                          <TableCell
                            sx={{
                              width: "80px",
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                            }}
                            onClick={() =>
                              onCellClick(project.id, "projectManager")
                            }
                          >
                            {editableCell &&
                            editableCell.projectId === project.id &&
                            editableCell.field === "projectManager" ? (
                              <TextField
                                value={project.projectManager}
                                onChange={(e) =>
                                  onInputChange(e, project.id, "projectManager")
                                }
                                onBlur={() =>
                                  onBlur(project.id, "projectManager")
                                }
                                autoFocus
                                size="small"
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "11px",
                                    padding: "4px 6px",
                                  },
                                }}
                              />
                            ) : (
                              project.projectManager
                            )}{" "}
                          </TableCell>

                          <TableCell
                            sx={{
                              width: "80px",
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              backgroundColor:
                                project.projectStatus === "Overdue"
                                  ? "lightcoral"
                                  : project.projectStatus === "Attention"
                                  ? "#f6d20e"
                                  : project.projectStatus === "In Progress"
                                  ? "#6798d9ff"
                                  : project.projectStatus === "Paid"
                                  ? "#10b981"
                                  : project.projectStatus === "Completed"
                                  ? "lightgreen"
                                  : project.projectStatus === "To be invoiced"
                                  ? "lightpink"
                                  : project.projectStatus === "Invoiced"
                                  ? "#ac95f1ff"
                                  : "inherit",
                              color: "white",
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            {project.projectStatus}
                          </TableCell>

                          <TableCell
                            sx={{
                              width: "120px",
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                              color: project.client ? "#1d4ed8" : "inherit",
                              cursor: project.client ? "pointer" : "default",
                              "&:hover": {
                                textDecoration: project.client
                                  ? "underline"
                                  : "none",
                                color: project.client ? "#1d4ed8" : "inherit",
                              },
                            }}
                            onClick={() =>
                              project.client &&
                              onClientNavigate?.(project.client)
                            }
                          >
                            {project.client}
                          </TableCell>

                          <TableCell
                            sx={{
                              width: "160px",
                              height: rowHeight,
                              fontSize: bodyFontSize,
                              padding: "0px",
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              textAlign: "center",
                            }}
                            onClick={() => onCellClick(project.id, "poc")}
                          >
                            {editableCell &&
                            editableCell.projectId === project.id &&
                            editableCell.field === "poc" ? (
                              <TextField
                                value={project.poc}
                                onChange={(e) =>
                                  onInputChange(e, project.id, "poc")
                                }
                                onBlur={() => onBlur(project.id, "poc")}
                                autoFocus
                                size="small"
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "11px",
                                    padding: "4px 6px",
                                  },
                                }}
                              />
                            ) : (
                              project.poc
                            )}{" "}
                          </TableCell>

                          {canSeeFinancials && (
                            <>
                              <TableCell
                                sx={{
                                  width: "70px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {formatMoney(project.income)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  width: "110px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "4px 8px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <span>
                                    {formatMoney(project.amountIncVat)}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    VAT @ {project.vat || 0}%
                                  </span>
                                </Box>
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: "80px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {deriveInvoiceRaised(
                                  project.dateForInvoice,
                                  project.invoiceRaised
                                )}
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: "80px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                                onClick={() =>
                                  onCellClick(project.id, "dateForInvoice")
                                }
                              >
                                {editableCell &&
                                editableCell.projectId === project.id &&
                                editableCell.field === "dateForInvoice" ? (
                                  <TextField
                                    type="date"
                                    value={dateformatter(
                                      convertTimestampToDate(
                                        project.dateForInvoice
                                      ),
                                      true
                                    )}
                                    onChange={(e) =>
                                      onInputChange(
                                        e,
                                        project.id,
                                        "dateForInvoice"
                                      )
                                    }
                                    onBlur={() =>
                                      onBlur(project.id, "dateForInvoice")
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: "11px",
                                        padding: "4px 6px",
                                      },
                                    }}
                                  />
                                ) : (
                                  dateformatter(
                                    convertTimestampToDate(
                                      project.dateForInvoice
                                    )
                                  )
                                )}{" "}
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: "100px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                                onClick={() =>
                                  onCellClick(project.id, "paymentStatus")
                                }
                              >
                                {editableCell &&
                                editableCell.projectId === project.id &&
                                editableCell.field === "paymentStatus" ? (
                                  <Select
                                    value={project.paymentStatus ?? ""}
                                    onChange={(e) =>
                                      onInputChange(
                                        e,
                                        project.id,
                                        "paymentStatus"
                                      )
                                    }
                                    onBlur={() =>
                                      onBlur(project.id, "paymentStatus")
                                    }
                                    autoFocus
                                    size="small"
                                    fullWidth
                                    displayEmpty
                                    renderValue={(selected) =>
                                      selected && selected.length > 0
                                        ? selected
                                        : "—"
                                    }
                                    sx={{
                                      "& .MuiSelect-select": {
                                        fontSize: "11px",
                                        padding: "4px 6px",
                                      },
                                    }}
                                  >
                                    <MenuItem value="">—</MenuItem>
                                    <MenuItem value="Pending">Pending</MenuItem>
                                    <MenuItem value="Paid">Paid</MenuItem>
                                  </Select>
                                ) : (
                                  renderPlaceholder(project.paymentStatus)
                                )}{" "}
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: "100px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                                onClick={() =>
                                  onCellClick(project.id, "paidOn")
                                }
                              >
                                {editableCell &&
                                editableCell.projectId === project.id &&
                                editableCell.field === "paidOn" ? (
                                  <TextField
                                    type="date"
                                    value={dateformatter(
                                      convertTimestampToDate(project.paidOn),
                                      true
                                    )}
                                    onChange={(e) =>
                                      onInputChange(e, project.id, "paidOn")
                                    }
                                    onBlur={() => onBlur(project.id, "paidOn")}
                                    InputLabelProps={{ shrink: true }}
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: "11px",
                                        padding: "4px 6px",
                                      },
                                    }}
                                  />
                                ) : (
                                  dateformatter(
                                    convertTimestampToDate(project.paidOn)
                                  )
                                )}{" "}
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: "100px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                                onClick={() =>
                                  onCellClick(project.id, "paymentNotes")
                                }
                              >
                                {editableCell &&
                                editableCell.projectId === project.id &&
                                editableCell.field === "paymentNotes" ? (
                                  <TextField
                                    value={project.paymentNotes}
                                    onChange={(e) =>
                                      onInputChange(
                                        e,
                                        project.id,
                                        "paymentNotes"
                                      )
                                    }
                                    onBlur={() =>
                                      onBlur(project.id, "paymentNotes")
                                    }
                                    autoFocus
                                    size="small"
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: "11px",
                                        padding: "4px 6px",
                                      },
                                    }}
                                  />
                                ) : (
                                  renderPlaceholder(project.paymentNotes)
                                )}{" "}
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: "100px",
                                  height: rowHeight,
                                  fontSize: bodyFontSize,
                                  padding: "0px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {(() => {
                                  const latestInvoice =
                                    getLatestInvoice(project);
                                  const invoiceUrlRaw =
                                    latestInvoice?.url ||
                                    latestInvoice?.downloadURL ||
                                    project.invoiceLink ||
                                    "";
                                  const normalizedInvoiceUrl =
                                    typeof invoiceUrlRaw === "string"
                                      ? invoiceUrlRaw.trim()
                                      : "";
                                  const invoiceLabel =
                                    latestInvoice?.name ||
                                    latestInvoice?.fileName ||
                                    "Invoice";
                                  const displayLabel =
                                    typeof invoiceLabel === "string"
                                      ? invoiceLabel.replace(/\.pdf$/i, "")
                                      : "Invoice";

                                  if (!normalizedInvoiceUrl)
                                    return emptyPlaceholder;

                                  return (
                                    <Typography
                                      component="a"
                                      href={normalizedInvoiceUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={displayLabel}
                                      sx={{
                                        color: "blue",
                                        cursor: "pointer",
                                        fontSize: bodyFontSize,
                                        textDecoration: "underline",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "inline-block",
                                        maxWidth: "100%",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (onInvoicePreview) {
                                          e.preventDefault();
                                          onInvoicePreview(
                                            normalizedInvoiceUrl,
                                            displayLabel
                                          );
                                        }
                                      }}
                                    >
                                      {displayLabel}
                                    </Typography>
                                  );
                                })()}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                        <TableRow>
                          <TableCell
                            style={{
                              paddingBottom: 0,
                              paddingTop: 0,
                              border: "none",
                            }}
                            colSpan={totalColumns}
                          >
                            <Collapse
                              in={expandedRows[project.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                mt={0}
                                mb={2}
                                mx={1}
                                sx={{
                                  width: "100%",
                                  pr: { xs: 1, md: 2 },
                                  backgroundColor: "#fff",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  component="div"
                                  sx={{
                                    fontFamily:
                                      "'Poppins', 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                    fontWeight: 700,
                                    color: "#0f172a",
                                    px: { xs: 1.5, md: 2 },
                                    pt: { xs: 1.25, md: 1.5 },
                                  }}
                                >
                                  Tasks
                                </Typography>
                                <TableContainer
                                  component={Box}
                                  sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    border: "1px solid #d7dde3",
                                    borderRadius: "12px",
                                    backgroundColor: "#fff",
                                    overflow: "hidden",
                                    boxShadow:
                                      "0 10px 30px -22px rgba(15,23,42,0.4)",
                                  }}
                                >
                                  <Table
                                    size="small"
                                    aria-label="tasks"
                                    sx={{
                                      width: "100%",
                                      tableLayout: "fixed",
                                      fontFamily: tableFontFamily,
                                      "& th": {
                                        fontSize: headerFontSize,
                                        fontWeight: 600,
                                        fontFamily: tableFontFamily,
                                      },
                                      "& td": {
                                        fontSize: bodyFontSize,
                                        fontFamily: tableFontFamily,
                                      },
                                    }}
                                  >
                                    <TableHead
                                      sx={{
                                        "& th": {
                                          fontSize: "12px",
                                          backgroundColor: "#0e4f7c",
                                          color: "#e6f2fb",
                                          fontWeight: 700,
                                          borderBottom: "1px solid #d7dde3",
                                        },
                                        "& th:first-of-type": {
                                          borderTopLeftRadius: "10px",
                                        },
                                        "& th:last-child": {
                                          borderTopRightRadius: "10px",
                                        },
                                      }}
                                    >
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            paddingLeft: firstColumnPadding / 2,
                                            width: taskColumnWidths.taskNo,
                                            textAlign: "center",
                                          }}
                                        >
                                          Task No
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            paddingLeft: subjectColumnPadding,
                                            width: taskColumnWidths.subject,
                                            maxWidth: taskColumnWidths.subject,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            textAlign: "center",
                                          }}
                                        >
                                          Subject
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            paddingLeft: countryColumnPadding,
                                            width: taskColumnWidths.country,
                                            maxWidth: taskColumnWidths.country,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            textAlign: "center",
                                          }}
                                        >
                                          Country
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width: taskColumnWidths.task,
                                            textAlign: "center",
                                          }}
                                        >
                                          Task
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width:
                                              taskColumnWidths.orderAccepted,
                                            textAlign: "center",
                                          }}
                                        >
                                          Order Accepted
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width: taskColumnWidths.dueDate,
                                            textAlign: "center",
                                          }}
                                        >
                                          Due Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width: taskColumnWidths.status,
                                            textAlign: "center",
                                          }}
                                        >
                                          Status
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width: taskColumnWidths.completedOn,
                                            textAlign: "center",
                                          }}
                                        >
                                          Completed On
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width: taskColumnWidths.analyst1,
                                            textAlign: "center",
                                          }}
                                        >
                                          Analyst 1
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            width: taskColumnWidths.analyst2,
                                            textAlign: "center",
                                          }}
                                        >
                                          Analyst 2
                                        </TableCell>
                                        {canSeeFinancials && (
                                          <>
                                            <TableCell
                                              sx={{
                                                width: taskColumnWidths.income,
                                                textAlign: "center",
                                              }}
                                            >
                                              Income
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                width: taskColumnWidths.vat,
                                                textAlign: "center",
                                              }}
                                            >
                                              VAT
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                width:
                                                  taskColumnWidths.amountIncVat,
                                                textAlign: "center",
                                              }}
                                            >
                                              Amount Inc VAT
                                            </TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {project.tasks &&
                                      project.tasks.length > 0 ? (
                                        project.tasks.map((task, taskIndex) => (
                                          <TableRow key={taskIndex}>
                                            <TableCell
                                              sx={{
                                                paddingLeft:
                                                  firstColumnPadding / 2,
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width: taskColumnWidths.taskNo,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "taskNo",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "taskNo" ? (
                                                <TextField
                                                  value={task.taskNo}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "taskNo",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "taskNo",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                task.taskNo
                                              )}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                maxWidth:
                                                  taskColumnWidths.subject,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                textAlign: "center",
                                                width: taskColumnWidths.subject,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "subject",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "subject" ? (
                                                <TextField
                                                  value={task.subject}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "subject",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "subject",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                task.subject
                                              )}
                                            </TableCell>

                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width: taskColumnWidths.country,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "country",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "country" ? (
                                                <TextField
                                                  value={task.country}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "country",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "country",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                task.country
                                              )}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width: taskColumnWidths.task,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "task",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "task" ? (
                                                <Select
                                                  value={task.task}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "task",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "task",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  fullWidth
                                                  size="small"
                                                  sx={{
                                                    fontSize: "11px",
                                                    "& .MuiSelect-select": {
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                >
                                                  {taskOptions.map((option) => (
                                                    <MenuItem
                                                      key={option}
                                                      value={option}
                                                      sx={{ fontSize: "11px" }}
                                                    >
                                                      {option}
                                                    </MenuItem>
                                                  ))}
                                                </Select>
                                              ) : (
                                                task.task
                                              )}
                                            </TableCell>

                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width:
                                                  taskColumnWidths.orderAccepted,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "orderAccepted",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "orderAccepted" ? (
                                                <TextField
                                                  type="date"
                                                  value={dateformatter(
                                                    convertTimestampToDate(
                                                      task.orderAccepted
                                                    ),
                                                    true
                                                  )}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "orderAccepted",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "orderAccepted",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                dateformatter(
                                                  convertTimestampToDate(
                                                    task.orderAccepted
                                                  )
                                                )
                                              )}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width: taskColumnWidths.dueDate,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "dueDate",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "dueDate" ? (
                                                <TextField
                                                  type="date"
                                                  value={dateformatter(
                                                    convertTimestampToDate(
                                                      task.dueDate
                                                    ),
                                                    true
                                                  )}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "dueDate",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "dueDate",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                dateformatter(
                                                  convertTimestampToDate(
                                                    task.dueDate
                                                  )
                                                )
                                              )}
                                            </TableCell>

                                            <TableCell
                                              style={{
                                                backgroundColor:
                                                  task.completedOn
                                                    ? "lightgreen"
                                                    : task.taskStatus ===
                                                      "Overdue"
                                                    ? "lightcoral"
                                                    : task.taskStatus ===
                                                      "Attention"
                                                    ? "#f6d20e"
                                                    : task.taskStatus ===
                                                      "In Progress"
                                                    ? "#6798d9ff"
                                                    : "inherit",
                                                color: "white",
                                                fontWeight: "900",
                                                textAlign: "center",
                                                width: taskColumnWidths.status,
                                              }}
                                            >
                                              {task.taskStatus}
                                            </TableCell>

                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width:
                                                  taskColumnWidths.completedOn,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "completedOn",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "completedOn" ? (
                                                <TextField
                                                  type="date"
                                                  value={dateformatter(
                                                    convertTimestampToDate(
                                                      task.completedOn
                                                    ),
                                                    true
                                                  )}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "completedOn",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "completedOn",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                dateformatter(
                                                  convertTimestampToDate(
                                                    task.completedOn
                                                  )
                                                )
                                              )}
                                            </TableCell>

                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width:
                                                  taskColumnWidths.analyst1,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "analyst1",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "analyst1" ? (
                                                <TextField
                                                  value={task.analyst1}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "analyst1",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "analyst1",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                task.analyst1
                                              )}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontSize: bodyFontSize,
                                                textAlign: "center",
                                                width:
                                                  taskColumnWidths.analyst2,
                                              }}
                                              onClick={() =>
                                                onCellClick(
                                                  project.id,
                                                  "analyst2",
                                                  taskIndex
                                                )
                                              }
                                            >
                                              {editableTaskCell &&
                                              editableTaskCell.projectId ===
                                                project.id &&
                                              editableTaskCell.taskIndex ===
                                                taskIndex &&
                                              editableTaskCell.field ===
                                                "analyst2" ? (
                                                <TextField
                                                  value={task.analyst2}
                                                  onChange={(e) =>
                                                    onInputChange(
                                                      e,
                                                      project.id,
                                                      "analyst2",
                                                      taskIndex
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    onBlur(
                                                      project.id,
                                                      "analyst2",
                                                      taskIndex
                                                    )
                                                  }
                                                  autoFocus
                                                  size="small"
                                                  sx={{
                                                    "& .MuiInputBase-input": {
                                                      fontSize: "11px",
                                                      padding: "4px 6px",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                task.analyst2
                                              )}
                                            </TableCell>

                                            {canSeeFinancials && (
                                              <>
                                                <TableCell
                                                  sx={{
                                                    fontSize: bodyFontSize,
                                                    textAlign: "center",
                                                    width:
                                                      taskColumnWidths.income,
                                                  }}
                                                  onClick={() =>
                                                    onCellClick(
                                                      project.id,
                                                      "income",
                                                      taskIndex
                                                    )
                                                  }
                                                >
                                                  {editableTaskCell &&
                                                  editableTaskCell.projectId ===
                                                    project.id &&
                                                  editableTaskCell.taskIndex ===
                                                    taskIndex &&
                                                  editableTaskCell.field ===
                                                    "income" ? (
                                                    <TextField
                                                      value={task.income}
                                                      onChange={(e) =>
                                                        onInputChange(
                                                          e,
                                                          project.id,
                                                          "income",
                                                          taskIndex
                                                        )
                                                      }
                                                      onBlur={() =>
                                                        onBlur(
                                                          project.id,
                                                          "income",
                                                          taskIndex
                                                        )
                                                      }
                                                      autoFocus
                                                      size="small"
                                                      sx={{
                                                        "& .MuiInputBase-input":
                                                          {
                                                            fontSize: "11px",
                                                            padding: "4px 6px",
                                                          },
                                                      }}
                                                    />
                                                  ) : (
                                                    formatMoney(task.income)
                                                  )}
                                                </TableCell>

                                                <TableCell
                                                  sx={{
                                                    fontSize: bodyFontSize,
                                                    textAlign: "center",
                                                    width: taskColumnWidths.vat,
                                                  }}
                                                  onClick={() =>
                                                    onCellClick(
                                                      project.id,
                                                      "vat",
                                                      taskIndex
                                                    )
                                                  }
                                                >
                                                  {editableTaskCell &&
                                                  editableTaskCell.projectId ===
                                                    project.id &&
                                                  editableTaskCell.taskIndex ===
                                                    taskIndex &&
                                                  editableTaskCell.field ===
                                                    "vat" ? (
                                                    <TextField
                                                      value={task.vat}
                                                      onChange={(e) =>
                                                        onInputChange(
                                                          e,
                                                          project.id,
                                                          "vat",
                                                          taskIndex
                                                        )
                                                      }
                                                      onBlur={() =>
                                                        onBlur(
                                                          project.id,
                                                          "vat",
                                                          taskIndex
                                                        )
                                                      }
                                                      autoFocus
                                                      size="small"
                                                      sx={{
                                                        "& .MuiInputBase-input":
                                                          {
                                                            fontSize: "11px",
                                                            padding: "4px 6px",
                                                          },
                                                      }}
                                                    />
                                                  ) : (
                                                    task.vat
                                                  )}
                                                  %
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    fontSize: bodyFontSize,
                                                    textAlign: "center",
                                                    width:
                                                      taskColumnWidths.amountIncVat,
                                                  }}
                                                >
                                                  {formatMoney(
                                                    task.amountIncVat
                                                  )}
                                                </TableCell>
                                              </>
                                            )}
                                          </TableRow>
                                        ))
                                      ) : (
                                        <TableRow>
                                          <TableCell colSpan={10}>
                                            No tasks available
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {loadMoreRef && (hasMore || isLoading || isLoadingMore) && (
                      <TableRow ref={loadMoreRef}>
                        <TableCell
                          colSpan={colWidths.length}
                          sx={{ p: 0, height: "40px" }}
                        >
                          {(isLoading || isLoadingMore) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                color: "#6b7280",
                                fontSize: "14px",
                              }}
                            >
                              Loading more...
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Popper
        open={Boolean(contextMenu)}
        anchorEl={contextMenuAnchorRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
            <div>
              <ClickAwayListener onClickAway={handleContextMenuClose}>
                <Box
                  sx={{
                    minWidth: 200,
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow:
                      "0 10px 40px -10px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                    py: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderBottom: "1px solid #f1f5f9",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Actions
                    </Typography>
                  </Box>
                  <Box
                    component="button"
                    onClick={() => handleContextMenuAction("edit")}
                    disabled={!canEditProjects || !onEditProject}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      border: "none",
                      backgroundColor: "transparent",
                      cursor:
                        canEditProjects && onEditProject
                          ? "pointer"
                          : "not-allowed",
                      opacity: canEditProjects && onEditProject ? 1 : 0.4,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor:
                          canEditProjects && onEditProject
                            ? "#f1f5f9"
                            : "transparent",
                      },
                      "&:active": {
                        backgroundColor:
                          canEditProjects && onEditProject
                            ? "#e2e8f0"
                            : "transparent",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <EditOutlined sx={{ fontSize: 18, color: "#3b82f6" }} />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#1e293b",
                          lineHeight: 1.3,
                        }}
                      >
                        Edit project
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#64748b",
                          lineHeight: 1.3,
                        }}
                      >
                        Modify project details
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    onClick={() => handleContextMenuAction("invoice")}
                    disabled={!canCreateInvoices || !onCreateInvoiceForProject}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      border: "none",
                      backgroundColor: "transparent",
                      cursor:
                        canCreateInvoices && onCreateInvoiceForProject
                          ? "pointer"
                          : "not-allowed",
                      opacity:
                        canCreateInvoices && onCreateInvoiceForProject
                          ? 1
                          : 0.4,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor:
                          canCreateInvoices && onCreateInvoiceForProject
                            ? "#f1f5f9"
                            : "transparent",
                      },
                      "&:active": {
                        backgroundColor:
                          canCreateInvoices && onCreateInvoiceForProject
                            ? "#e2e8f0"
                            : "transparent",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: "#f0fdf4",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ReceiptLongOutlined
                        sx={{ fontSize: 18, color: "#22c55e" }}
                      />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#1e293b",
                          lineHeight: 1.3,
                        }}
                      >
                        Create invoice
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#64748b",
                          lineHeight: 1.3,
                        }}
                      >
                        Generate new invoice
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    onClick={() => handleContextMenuAction("summary")}
                    disabled={!onGenerateSummary}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      border: "none",
                      backgroundColor: "transparent",
                      cursor: onGenerateSummary ? "pointer" : "not-allowed",
                      opacity: onGenerateSummary ? 1 : 0.4,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: onGenerateSummary
                          ? "#f1f5f9"
                          : "transparent",
                      },
                      "&:active": {
                        backgroundColor: onGenerateSummary
                          ? "#e2e8f0"
                          : "transparent",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: "#fef3c7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SummarizeOutlined
                        sx={{ fontSize: 18, color: "#d97706" }}
                      />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#1e293b",
                          lineHeight: 1.3,
                        }}
                      >
                        Generate summary
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#64748b",
                          lineHeight: 1.3,
                        }}
                      >
                        Create project summary
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default ProjectTable;
